import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi } from '../../api/adminApi';

/**
 * Scenario Evals hub — every dataset in one grid. Rendered as a tab of the
 * Evals page, so it carries no heading of its own.
 *
 * Benches are declared in code (`eval/benchRegistry.ts`) and materialize on
 * first load; the rest are the older platform-eval datasets and the auto-*
 * flag feeds. They're distinguished by colour rather than by section, because
 * the auto-* feeds carry real flagged failures and relegating them below a
 * fold would bury the most useful rows on the page.
 */
export default function ScenarioEvals() {
    const benchQ = useQuery({ queryKey: ['benches'], queryFn: () => adminApi.listBenches() });
    const { data, isLoading, error } = useQuery({
        queryKey: ['evalDatasets'],
        queryFn: () => adminApi.getEvalDatasets(),
    });

    if (isLoading) return <div className="se-loading">Loading datasets…</div>;
    if (error) return <div className="se-error">Error: {error instanceof Error ? error.message : 'failed to load'}</div>;

    const benches = benchQ.data?.benches ?? [];
    const benchIds = new Set(benches.map(b => b.datasetId));

    // Benches first, then the rest alphabetically — one list, ordered.
    const cards = [
        ...benches.map(b => ({
            key: b.datasetId, to: b.datasetId, name: b.name, readableId: b.readableId,
            description: b.description, badges: ['bench', b.operation], isBench: true,
        })),
        ...(data?.datasets ?? [])
            .filter(d => !benchIds.has(d.id))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(d => ({
                key: d.id, to: d.id, name: d.name, readableId: d.readableId,
                description: d.description, badges: [d.category], isBench: false,
            })),
    ];

    return (
        <div className="se-page">
            <div className="se-header">
                <p className="se-sub">Judge what each prompt execution produced, and compare configurations.</p>
            </div>

            <div className="se-grid">
                {cards.map(c => (
                    <Link key={c.key} to={`/scenario-evals/${c.to}`} className={`se-card${c.isBench ? ' bench' : ''}`}>
                        <h3>{c.name}</h3>
                        <code className="se-rid">{c.readableId}</code>
                        <p className="se-desc">{c.description || '—'}</p>
                        <div className="se-card-foot">
                            {c.badges.filter(Boolean).map(b => (
                                <span key={b} className={`se-badge${b === 'bench' ? ' is-bench' : ''}`}>{b}</span>
                            ))}
                        </div>
                    </Link>
                ))}
            </div>

            <style>{`
                .se-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
                .se-sub { color: var(--text-secondary); margin: 0; font-size: 14px; }
                .se-loading, .se-error { padding: 8px 0; color: var(--text-secondary); }
                .se-error { color: var(--status-error-text); }

                .se-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; }

                /* A card is a link, so every state has to kill the UA underline
                   explicitly — :focus and :active otherwise underline the whole card. */
                .se-card,
                .se-card:hover,
                .se-card:focus,
                .se-card:active,
                .se-card:visited,
                .se-card * { text-decoration: none; }

                .se-card {
                    display: block; border: 1px solid var(--border-color); border-left: 3px solid var(--border-color);
                    border-radius: 8px; padding: 16px; color: inherit;
                    background: var(--bg-secondary); transition: border-color .15s;
                }
                .se-card:hover { border-color: var(--color-primary); }
                /* Colour is what separates a bench from the rest — not position. */
                .se-card.bench { border-left-color: var(--color-primary); }

                .se-card h3 { margin: 0 0 4px; font-size: 16px; color: var(--text-primary); }
                .se-rid { font-size: 12px; color: var(--text-tertiary); }
                .se-desc { color: var(--text-secondary); font-size: 13px; margin: 8px 0; min-height: 32px; }
                .se-card-foot { display: flex; gap: 6px; flex-wrap: wrap; }
                .se-badge {
                    font-size: 11px; padding: 2px 8px; border-radius: 999px;
                    background: var(--bg-tertiary); color: var(--text-secondary);
                }
                .se-badge.is-bench { background: var(--color-primary); color: #fff; }
            `}</style>
        </div>
    );
}
